import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { PORTAL_LINKS } from "./constants/site";

// Layout Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import ConversionAnalyticsTracker from "./components/ConversionAnalyticsTracker";
import TrackingInstall from "./components/TrackingInstall";

// Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Calculator from "./pages/Calculator";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Disclaimer from "./pages/Disclaimer";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Complaints from "./pages/Complaints";
import TruckFinance from "./pages/TruckFinance";
import BusinessVehicleFinance from "./pages/BusinessVehicleFinance";
import PlantMachineryFinance from "./pages/PlantMachineryFinance";
import ApplyQuick from "./pages/ApplyQuick";
import CarLoansPage from "./pages/CarLoansPage";
import PersonalLoansPage from "./pages/PersonalLoansPage";
import LeisureFinancePage from "./pages/LeisureFinancePage";
import CommercialCarLoansPage from "./pages/CommercialCarLoansPage";
import DisclosurePage from "./pages/DisclosurePage";
import HowItWorksPage from "./pages/HowItWorksPage";
import ReviewsPage from "./pages/ReviewsPage";
import QuoteConfirmationPage from "./pages/QuoteConfirmationPage";
import NotFoundPage from "./pages/NotFoundPage";

// SEO Landing Pages (dynamic)
import SeoPage from "./pages/seo/SeoPage";
import CommercialIntentPage from "./pages/seo/CommercialIntentPage";
import LocationsIndexPage from "./pages/seo/LocationsIndexPage";
import LearnHubPage from "./pages/seo/LearnHubPage";
import LearnArticlePage from "./pages/seo/LearnArticlePage";
import AuthorProfilePage from "./pages/seo/AuthorProfilePage";
import { ALL_COMMERCIAL_PAGES } from "./data/seoRetrofitData";

// Calculator Pages
import EquipmentFinanceCalculator from "./pages/calculators/EquipmentFinanceCalculator";
import TruckFinanceCalculator from "./pages/calculators/TruckFinanceCalculator";
import LeaseVsBuyCalculator from "./pages/calculators/LeaseVsBuyCalculator";
import AssetRefinanceCalculator from "./pages/calculators/AssetRefinanceCalculator";
import CalculatorsHub from "./pages/calculators/CalculatorsHub";

// Authority Pages
import EquipmentFinanceGuide from "./pages/authority/EquipmentFinanceGuide";

// FlashIQ CRM
import { AuthProvider } from "./components/CRM/AuthContext";
import ProtectedRoute from "./components/CRM/ProtectedRoute";
import FlashIQLogin from "./pages/CRM/Login";
import CRMDashboard from "./pages/CRM/Dashboard";
import Pipeline from "./pages/CRM/Pipeline";
import DealDetail from "./pages/CRM/DealDetail";
import DealWorkspace from "./pages/CRM/DealWorkspace";
import NewDeal from "./pages/CRM/NewDeal";
import Analytics from "./pages/CRM/Analytics";
import Commissions from "./pages/CRM/Commissions";
import Settings from "./pages/CRM/Settings";
import ApplicationPortal from "./pages/Portal/ApplicationPortal";
import ApplyAnalyticsDashboard from "./pages/Admin/ApplyAnalyticsDashboard";
import HomeLoanReferrals from "./pages/Admin/HomeLoanReferrals";

const STATE_CAPITALS = {
  nsw: 'sydney', vic: 'melbourne', qld: 'brisbane', wa: 'perth', sa: 'adelaide',
  tas: 'hobart', act: 'canberra', nt: 'darwin', 'regional-nsw': 'sydney', 'regional-qld': 'brisbane',
};
const LEGACY_LOCATION_REDIRECTS = {
  '/equipment-finance-by-state': '/locations',
  ...Object.fromEntries(Object.entries(STATE_CAPITALS).flatMap(([state, city]) => [
    [`/equipment-finance-${state}`, `/locations/${city}`],
    [`/truck-finance-${state}`, `/locations/${city}`],
  ])),
  ...Object.fromEntries(['sydney', 'melbourne', 'brisbane', 'perth', 'adelaide'].map((city) => [`/truck-finance-${city}`, `/locations/${city}`])),
};

function LegacyPortalRedirect() {
  const { token } = useParams();
  return <Navigate to={`/application/${token}`} replace />;
}

function LegacyBlogRedirect() {
  const { slug } = useParams();
  return <Navigate to={slug ? `/learn/${slug}` : '/learn'} replace />;
}

function ExternalPortalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);

  return null;
}

// Layout wrapper to conditionally show header/footer
function Layout({ children }) {
  const location = useLocation();
  const isCRM = location.pathname.startsWith('/crm') || location.pathname.startsWith('/portal') || location.pathname.startsWith('/application') || location.pathname.startsWith('/admin');
  
  return (
    <>
      {!isCRM && <ConversionAnalyticsTracker />}
      {!isCRM && <Header />}
      <main>{children}</main>
      {!isCRM && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Layout>
            <TrackingInstall />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/calculator" element={<Calculator />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/testimonials" element={<Navigate to="/reviews" replace />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/disclosure" element={<DisclosurePage />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/complaints" element={<Complaints />} />
              <Route path="/truck-finance-australia" element={<TruckFinance />} />
              <Route path="/business-vehicle-finance" element={<BusinessVehicleFinance />} />
              <Route path="/plant-machinery-finance" element={<PlantMachineryFinance />} />
              <Route path="/apply" element={<ApplyQuick />} />
              <Route path="/car-loans" element={<CarLoansPage />} />
              <Route path="/commercial-car-loans" element={<CommercialCarLoansPage />} />
              <Route path="/personal-loans" element={<PersonalLoansPage />} />
              <Route path="/leisure-finance" element={<LeisureFinancePage />} />
              <Route path="/home-loans" element={<Navigate to="/" replace />} />
              <Route path="/quote-confirmation" element={<QuoteConfirmationPage />} />

              {ALL_COMMERCIAL_PAGES.map((page) => (
                <Route
                  key={page.path}
                  path={page.path}
                  element={<CommercialIntentPage pagePath={page.path} />}
                />
              ))}

              <Route path="/locations" element={<LocationsIndexPage />} />
              <Route path="/lenders" element={<Navigate to="/" replace />} />
              <Route path="/lenders/:slug" element={<Navigate to="/" replace />} />
              <Route path="/learn" element={<LearnHubPage />} />
              <Route path="/learn/:slug" element={<LearnArticlePage />} />
              <Route path="/authors/:slug" element={<AuthorProfilePage />} />

              {/* Consolidated location pages: old state and city landing pages redirect to /locations */}
              {Object.entries(LEGACY_LOCATION_REDIRECTS).map(([from, to]) => (
                <Route key={from} path={from} element={<Navigate to={to} replace />} />
              ))}

              {/* Backward compatibility redirects */}
              <Route path="/blog" element={<Navigate to="/learn" replace />} />
              <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
              <Route path="/truck-finance" element={<Navigate to="/truck-and-trailer-finance" replace />} />
              <Route path="/commercial-loans" element={<Navigate to="/business-loans" replace />} />
              <Route path="/business-finance" element={<Navigate to="/business-loans" replace />} />
              <Route path="/commercial-asset-finance" element={<Navigate to="/equipment-finance" replace />} />
              <Route path="/equipment-finance-sydney" element={<Navigate to="/locations/sydney" replace />} />
              <Route path="/equipment-finance-melbourne" element={<Navigate to="/locations/melbourne" replace />} />
              <Route path="/equipment-finance-brisbane" element={<Navigate to="/locations/brisbane" replace />} />
              <Route path="/equipment-finance-perth" element={<Navigate to="/locations/perth" replace />} />
              <Route path="/equipment-finance-adelaide" element={<Navigate to="/locations/adelaide" replace />} />

              <Route path="/calculators/asset-finance-calculator" element={<EquipmentFinanceCalculator />} />
              <Route path="/calculators/chattel-mortgage-calculator" element={<LeaseVsBuyCalculator />} />
              <Route path="/calculators/business-loan-repayment-calculator" element={<AssetRefinanceCalculator />} />

              <Route path="/consumer-asset-finance" element={<Navigate to="/vehicle-finance" replace />} />
              <Route path="/apply-now" element={<ExternalPortalRedirect to={PORTAL_LINKS.GENERIC} />} />
              <Route path="/finance-finder" element={<Navigate to="/" replace />} />
              
              {/* SEO Landing Pages - Asset Finance (10) */}
              <Route path="/machinery-finance" element={<SeoPage />} />
              <Route path="/construction-equipment-finance" element={<SeoPage />} />
              <Route path="/commercial-vehicle-finance" element={<SeoPage />} />
              <Route path="/agriculture-equipment-finance" element={<SeoPage />} />
              <Route path="/medical-equipment-finance" element={<SeoPage />} />
              <Route path="/it-equipment-finance" element={<SeoPage />} />
              <Route path="/plant-and-equipment-finance" element={<SeoPage />} />
              <Route path="/business-equipment-finance" element={<SeoPage />} />
              {/* SEO Landing Pages - Finance Products (6) */}
              <Route path="/chattel-mortgage" element={<SeoPage />} />
              <Route path="/finance-lease" element={<SeoPage />} />
              <Route path="/operating-lease" element={<SeoPage />} />
              <Route path="/hire-purchase" element={<SeoPage />} />
              <Route path="/asset-refinance" element={<SeoPage />} />
              <Route path="/low-doc-asset-finance" element={<SeoPage />} />
              {/* SEO Landing Pages - Industry (10) */}
              <Route path="/finance-for-builders" element={<SeoPage />} />
              <Route path="/finance-for-electricians" element={<SeoPage />} />
              <Route path="/finance-for-plumbers" element={<SeoPage />} />
              <Route path="/finance-for-tradies" element={<SeoPage />} />
              <Route path="/finance-for-transport-companies" element={<SeoPage />} />
              <Route path="/finance-for-farmers" element={<SeoPage />} />
              <Route path="/finance-for-construction-companies" element={<SeoPage />} />
              <Route path="/finance-for-dentists" element={<SeoPage />} />
              <Route path="/finance-for-medical-clinics" element={<SeoPage />} />
              <Route path="/finance-for-manufacturers" element={<SeoPage />} />
              {/* Interactive Calculators */}
              <Route path="/calculators" element={<CalculatorsHub />} />
              <Route path="/equipment-finance-calculator" element={<EquipmentFinanceCalculator />} />
              <Route path="/truck-finance-calculator" element={<TruckFinanceCalculator />} />
              <Route path="/lease-vs-buy-calculator" element={<LeaseVsBuyCalculator />} />
              <Route path="/asset-refinance-calculator" element={<AssetRefinanceCalculator />} />
              {/* Authority Pages */}
              <Route path="/australian-equipment-finance-statistics" element={<Navigate to="/equipment-finance-guide-australia" replace />} />
              <Route path="/equipment-finance-guide-australia" element={<EquipmentFinanceGuide />} />
              {/* Legacy Location Pages (redirect to current routes) */}
              <Route path="/commercial-finance-brisbane" element={<Navigate to="/locations/brisbane" replace />} />
              <Route path="/asset-finance-perth" element={<Navigate to="/locations/perth" replace />} />
              
              {/* FlashIQ CRM Routes */}
              <Route path="/crm/login" element={<FlashIQLogin />} />
              <Route path="/crm" element={<ProtectedRoute><CRMDashboard /></ProtectedRoute>} />
              <Route path="/crm/pipeline" element={<ProtectedRoute><Pipeline /></ProtectedRoute>} />
              <Route path="/crm/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
              <Route path="/crm/commissions" element={<ProtectedRoute><Commissions /></ProtectedRoute>} />
              <Route path="/crm/deals/new" element={<ProtectedRoute><NewDeal /></ProtectedRoute>} />
              <Route path="/crm/deals/:dealId" element={<ProtectedRoute><DealDetail /></ProtectedRoute>} />
              <Route path="/crm/deals/:dealId/workspace" element={<ProtectedRoute><DealWorkspace /></ProtectedRoute>} />
              <Route path="/crm/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/admin/analytics" element={<ProtectedRoute><ApplyAnalyticsDashboard /></ProtectedRoute>} />
              <Route path="/admin/home-loan-referrals" element={<ProtectedRoute><HomeLoanReferrals /></ProtectedRoute>} />
              <Route path="/application/:token" element={<ApplicationPortal />} />
              <Route path="/portal/apply/:token" element={<LegacyPortalRedirect />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Layout>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
